import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SeriesHeader from '../components/brand/SeriesHeader';
import SeriesFooter from '../components/brand/SeriesFooter';
import { VisibilityGuidedInput, GatedImpactDisplay } from '../components/visibility-hero';
import { getSeriesBySlug } from '../data/series';
import { Button } from '../components/ui/button';
import { 
  Loader2, 
  Eye, 
  EyeOff, 
  TrendingUp, 
  Users, 
  Award,
  ArrowRight,
  CheckCircle,
  Sparkles,
  AlertTriangle,
  Clock,
  Target,
  Frown
} from 'lucide-react';

/**
 * HP.1: "Your Work Isn't Being Seen" Hero Page
 * 
 * Target: Professionals whose contributions go unnoticed
 * Hook: "You're doing more than your manager realises"
 * Maps to: Living CV + Impact tracking
 * CTA: "Describe your last achievement"
 */

const VisibilityHeroPage = () => {
  const navigate = useNavigate();
  
  const [flowState, setFlowState] = useState('hero');
  const [inputData, setInputData] = useState(null);
  const [impactData, setImpactData] = useState(null);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Purple accent for visibility theme
  const series = {
    name: 'Impact Visibility',
    accentColor: '#8b5cf6',
    slug: 'visibility',
    author: { name: 'Paul Hooper' }
  };

  useEffect(() => {
    const pendingUnlock = sessionStorage.getItem('rv_pending_unlock');
    if (pendingUnlock) {
      const data = JSON.parse(pendingUnlock);
      if (data.flow_type === 'visibility') {
        navigate(`/visibility/complete?rv_id=${data.review_id}&hash=${data.input_hash}`);
      }
    }
  }, []);

  const handleStartInput = () => {
    setFlowState('input');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputComplete = async (data) => {
    setInputData(data);
    setFlowState('generating');
    setError(null);

    try {
      // Reuse the review generation API with visibility flow_type
      const response = await fetch(`${API_URL}/api/review/generate-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: data.role,
          context: data.context,
          achievements: data.achievements,
          challenges: data.obstacles || '',
          growth: data.who_benefited || '',
          goals: data.recognition_gap || '',
          optional_paste: data.optional_paste || '',
          flow_type: 'visibility'
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to generate impact analysis');
      }

      const result = await response.json();
      setImpactData(result);
      setFlowState('gated');

    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message);
      setFlowState('input');
    }
  };

  const handleInputCancel = () => {
    setFlowState('hero');
    setInputData(null);
  };

  const handleUnlock = (returnUrl) => {
    navigate(`/login?return=${encodeURIComponent(returnUrl)}`);
  };

  const handleStartFresh = () => {
    setFlowState('input');
    setImpactData(null);
    setInputData(null);
    sessionStorage.removeItem('rv_input');
    sessionStorage.removeItem('rv_pending_unlock');
  };

  const renderMainContent = () => {
    switch (flowState) {
      case 'input':
        return (
          <section className="min-h-screen bg-gradient-to-br from-violet-950 via-slate-900 to-slate-900 flex items-center justify-center py-20 px-6">
            <VisibilityGuidedInput
              onComplete={handleInputComplete}
              onCancel={handleInputCancel}
              accentColor={series.accentColor}
            />
          </section>
        );

      case 'generating':
        return (
          <section className="min-h-screen bg-gradient-to-br from-violet-950 via-slate-900 to-slate-900 flex items-center justify-center">
            <div className="text-center text-white" data-testid="vis-generating">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-violet-400" />
              <h2 className="text-xl font-semibold mb-2">Analyzing your impact...</h2>
              <div className="space-y-2 text-slate-400 text-sm">
                <p className="animate-pulse">Finding the story in your work...</p>
                <p className="animate-pulse delay-75">Identifying stakeholder value...</p>
                <p className="animate-pulse delay-150">Crafting your impact narrative...</p>
              </div>
            </div>
          </section>
        );

      case 'gated':
        return (
          <section className="min-h-screen bg-slate-100 py-20 px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Your impact, made visible
              </h2>
              <p className="text-slate-600">
                Here's what your manager should know
              </p>
            </div>
            <GatedImpactDisplay
              impactData={impactData}
              onUnlock={handleUnlock}
              onStartFresh={handleStartFresh}
              accentColor={series.accentColor}
            />
          </section>
        );

      default:
        return (
          <>
            <VisibilityHero series={series} onStartInput={handleStartInput} />
            <VisibilityPainPoints series={series} />
            <VisibilityHowItWorks series={series} />
            <VisibilityDemo series={series} />
            <VisibilityFinalCTA series={series} onStartInput={handleStartInput} />
          </>
        );
    }
  };

  return (
    <div className="visibility-hero-page min-h-screen bg-slate-50" data-testid="visibility-hero-page">
      <SeriesHeader series={series} />
      
      <main>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-center">
            {error}
            <button onClick={() => setError(null)} className="ml-4 underline">Dismiss</button>
          </div>
        )}
        {renderMainContent()}
      </main>
      
      {flowState === 'hero' && <SeriesFooter series={series} />}
    </div>
  );
};

/**
 * Hero Section
 */
const VisibilityHero = ({ series, onStartInput }) => {
  const accentColor = series?.accentColor || '#8b5cf6';

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden pt-20" data-testid="visibility-hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-violet-950 via-slate-900 to-slate-900">
        <div 
          className="absolute top-20 right-20 w-[500px] h-[500px] rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: accentColor }}
        />
        <div 
          className="absolute bottom-40 left-20 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ backgroundColor: '#3b82f6' }}
        />
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="h-full w-full" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-8"
            style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
          >
            <EyeOff className="w-4 h-4" />
            The curse of being competent
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            <span className="text-white">Your work </span>
            <span style={{ color: accentColor }}>isn't being seen</span>
          </h1>

          <p className="text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            You're doing more than your manager realises. 
            <span className="text-white font-medium"> Let's make your impact visible</span> — with evidence, not self-promotion.
          </p>

          <button
            onClick={onStartInput}
            className="inline-flex items-center gap-3 px-8 py-4 text-lg font-medium text-white rounded-xl transition-all hover:scale-105 shadow-lg"
            style={{ backgroundColor: accentColor }}
            data-testid="vis-start-btn"
          >
            <Eye className="w-5 h-5" />
            Make My Work Visible
            <ArrowRight className="w-5 h-5" />
          </button>

          <div className="flex items-center justify-center gap-8 mt-10 text-slate-400">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-sm">No signup to start</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <span className="text-sm">AI-powered</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-violet-400" />
              <span className="text-sm">Evidence-based</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Pain Points Section
 */
const VisibilityPainPoints = ({ series }) => {
  const painPoints = [
    {
      icon: EyeOff,
      title: "Your best work happens behind the scenes",
      description: "You fix things before they break. You prevent problems. But prevention is invisible."
    },
    {
      icon: Users,
      title: "Credit goes to the loud ones",
      description: "The person who presents gets the praise. You built it, but nobody knows."
    },
    {
      icon: Clock,
      title: "No time to document wins",
      description: "You're too busy doing the work to tell anyone about it."
    },
    {
      icon: Frown,
      title: "Self-promotion feels gross",
      description: "You don't want to brag. But staying quiet means staying invisible."
    },
    {
      icon: AlertTriangle,
      title: "Impact gets lost in translation",
      description: "You saved 20 hours. Fixed a critical bug. But it's hard to explain."
    },
    {
      icon: Target,
      title: "Reviews don't capture reality",
      description: "Your annual review is a vague summary of 12 months of actual work."
    }
  ];

  return (
    <section className="py-20 bg-slate-50" data-testid="visibility-pain-points">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Sound familiar?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Good work is often quiet work. The problem? Quiet doesn't get noticed.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {painPoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-xl border border-slate-200 hover:border-violet-200 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-slate-100 group-hover:bg-violet-50 transition-colors">
                    <Icon className="w-6 h-6 text-slate-500 group-hover:text-violet-500 transition-colors" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{point.title}</h3>
                  <p className="text-sm text-slate-600">{point.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-16 text-center">
            <p className="text-lg text-slate-700">
              <span className="font-semibold" style={{ color: series.accentColor }}>There's a better way.</span>
              {' '}Turn your quiet wins into visible impact.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * How It Works Section
 */
const VisibilityHowItWorks = ({ series }) => {
  const steps = [
    {
      number: '01',
      title: 'Describe your work',
      description: 'Tell us about an achievement that deserves more recognition. No polish needed.',
      icon: Award
    },
    {
      number: '02',
      title: 'We find the impact',
      description: 'Our AI extracts the stakeholder value, skills demonstrated, and business impact.',
      icon: TrendingUp
    },
    {
      number: '03',
      title: 'Get a visibility snapshot',
      description: 'Receive a clear, compelling summary you can share with your manager or keep for reviews.',
      icon: Eye
    }
  ];

  return (
    <section className="py-20 bg-white" data-testid="visibility-how-it-works">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 rounded-full text-sm text-violet-700 mb-6">
              <Clock className="w-4 h-4" />
              <span>Takes 3 minutes</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How it works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              No self-promotion required. We help you articulate impact you already created.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div 
                  key={step.number}
                  className="relative bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div 
                    className="absolute -top-3 left-6 px-3 py-1 rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: series.accentColor }}
                  >
                    Step {step.number}
                  </div>

                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 mt-2"
                    style={{ backgroundColor: `${series.accentColor}10` }}
                  >
                    <Icon className="w-7 h-7" style={{ color: series.accentColor }} />
                  </div>

                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Demo Output Section
 */
const VisibilityDemo = ({ series }) => {
  return (
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6"
              style={{ backgroundColor: `${series.accentColor}10`, color: series.accentColor }}
            >
              <Eye className="w-4 h-4" />
              See what you'll get
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Your impact, crystallized
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From scattered achievements to a clear impact story your manager can understand.
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${series.accentColor}15` }}
                >
                  <Eye className="w-5 h-5" style={{ color: series.accentColor }} />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Impact Snapshot</p>
                  <p className="text-sm text-slate-500">Example output</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium">The Story</h4>
                <p className="text-slate-800 leading-relaxed">
                  Behind the scenes, you've been the stability anchor for the engineering team. Your proactive approach to technical debt prevented at least 3 major incidents this quarter, while your mentoring has accelerated the growth of junior developers into independent contributors.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-medium">Key Contributions</h4>
                <div className="bg-gradient-to-r from-violet-50 to-slate-50 rounded-xl p-4 border border-violet-100">
                  <p className="font-medium text-slate-900 mb-1">Production Incident Prevention</p>
                  <p className="text-sm text-slate-600 mb-2">
                    Identified and fixed a critical memory leak before it caused customer-facing issues. Estimated impact: prevented 4+ hours of downtime.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="px-2 py-0.5 bg-white border border-violet-200 rounded text-xs text-violet-700">Technical Excellence</span>
                    <span className="px-2 py-0.5 bg-white border border-violet-200 rounded text-xs text-violet-700">Proactive Problem Solving</span>
                  </div>
                </div>
              </div>

              <p className="text-center text-sm text-slate-500">
                + stakeholder impact, skills demonstrated, and manager talking points in the full analysis
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Final CTA Section
 */
const VisibilityFinalCTA = ({ series, onStartInput }) => {
  const navigate = useNavigate();

  return (
    <section className="py-20 bg-violet-950" data-testid="visibility-final-cta">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-violet-900 to-slate-900 rounded-3xl p-8 lg:p-12 border border-violet-800 relative overflow-hidden">
            <div 
              className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-20"
              style={{ backgroundColor: series.accentColor }}
            />
            
            <div className="relative z-10">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm text-white mb-6">
                  <Sparkles className="w-4 h-4" />
                  <span>Stop being invisible</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Your work deserves to be seen
                </h2>
                <p className="text-lg text-violet-200 max-w-2xl mx-auto">
                  Turn your quiet contributions into visible impact. No bragging required — just evidence.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={onStartInput}
                  size="lg"
                  className="px-8 py-4 text-white text-lg font-medium"
                  style={{ backgroundColor: series.accentColor }}
                  data-testid="vis-cta-start"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Make My Work Visible
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/review')}
                  className="px-8 py-4 text-white border-white/30 hover:bg-white/10"
                >
                  Need a performance review instead?
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VisibilityHeroPage;
